import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { useRedirectIfAuthenticated } from '../../auth/useRedirectIfAuthenticated';
import ThemeToggle from '../../theme/ThemeToggle';
import { NAV_LINKS, PLAY_STORE_URL } from './home-content';
import {
  ClosingSection,
  FeatureSection,
  HelpSection,
  HeroSection,
  PossibilitiesSection,
  PricingSection,
  ScenarioSection,
  ScreenshotSection,
  SetupSection,
} from './home-sections';
import { MOCK_SECTION_DELAY_MS, useMockSection } from './use-mock-section';
import './home-page.css';

export type SharedContentKind = 'point' | 'playlist' | 'itinerary';

const SHARE_NOTICE: Record<SharedContentKind, string> = {
  point: 'Someone shared a location with you. Open it in MockLoc, or get the app from Play Store.',
  playlist: 'Someone shared a playlist with you. Open it in MockLoc, or get the app from Play Store.',
  itinerary: 'Someone shared an itinerary with you. Open it in MockLoc, or get the app from Play Store.',
};

type HomePageViewProps = {
  skipAuthRedirect?: boolean;
  shareKind?: SharedContentKind;
  openInAppHref?: string;
};

const HomePageView: React.FC<HomePageViewProps> = ({
  skipAuthRedirect = false,
  shareKind,
  openInAppHref,
}) => {
  const { status } = useAuth();
  const isLoggedIn = status === 'authenticated';
  useRedirectIfAuthenticated(!skipAuthRedirect);
  const [active, setActive] = useState('top');
  const shareNotice = shareKind ? SHARE_NOTICE[shareKind] : null;
  const accountTo = isLoggedIn ? '/app' : '/login';
  const accountLabel = isLoggedIn ? 'My account' : 'Sign in';
  const pricingTo = isLoggedIn ? '/app' : '/login?mode=signup';
  const pricingLabel = isLoggedIn ? 'Open my account' : 'Create free account';

  const heroStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const shotStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const featureStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const setupStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const scenarioStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const bandStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const pricingStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const helpStatus = useMockSection(MOCK_SECTION_DELAY_MS);
  const closeStatus = useMockSection(MOCK_SECTION_DELAY_MS);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const nodes = NAV_LINKS.map((link) => document.getElementById(link.id)).filter(
      (node): node is HTMLElement => node instanceof HTMLElement
    );
    if (nodes.length === 0) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target.id) {
          setActive(visible.target.id);
        }
      },
      { rootMargin: '-18% 0px -55% 0px', threshold: [0.1, 0.25, 0.5] }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="ml-root" data-screen="guest-home">
      <header className="ml-nav">
        <div className="ml-wrap ml-nav__inner">
          <Link to="/" className="ml-brand">
            <img src="/mockloc-icon.png" alt="" className="ml-brand__logo" />
            <span className="ml-brand__name">MockLoc</span>
          </Link>
          <nav className="ml-nav__links" aria-label="Page">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={link.href}
                className={active === link.id ? 'is-active' : undefined}
                aria-current={active === link.id ? 'true' : undefined}
                onClick={() => setActive(link.id)}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="ml-nav__actions">
            <ThemeToggle />
            <Link to={accountTo} className="ml-signin" id="nav-login">
              <UserRound size={16} aria-hidden="true" />
              {accountLabel}
            </Link>
          </div>
        </div>
      </header>

      <main>
        {shareNotice && (
          <div className="ml-wrap ml-share-wrap">
            <div className="ml-share-notice" role="status">
              <p>{shareNotice}</p>
              {openInAppHref && (
                <a id="share-open-in-app" href={openInAppHref} className="ml-signin">
                  Open in MockLoc app
                </a>
              )}
            </div>
          </div>
        )}
        <HeroSection status={heroStatus} />
        <ScreenshotSection status={shotStatus} />
        <FeatureSection status={featureStatus} />
        <SetupSection status={setupStatus} />
        <ScenarioSection status={scenarioStatus} />
        <PossibilitiesSection status={bandStatus} />
        <PricingSection status={pricingStatus} actionTo={pricingTo} actionLabel={pricingLabel} />
        <HelpSection status={helpStatus} />
        <ClosingSection status={closeStatus} />
      </main>

      <footer className="ml-footer">
        <div className="ml-wrap ml-footer__inner">
          <Link to="/" className="ml-brand">
            <img src="/mockloc-icon.png" alt="" className="ml-brand__logo" />
            <span className="ml-brand__name">MockLoc</span>
          </Link>
          <p>© {new Date().getFullYear()} MockLoc · Mock location for Android</p>
          <div className="ml-footer__links">
            <Link to="/articles">Articles</Link>
            <a href="/app-ads.txt" id="footer-app-ads">
              app-ads.txt
            </a>
            <a id="footer-playstore" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              Play Store
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePageView;
